import React, { useState, useEffect } from 'react'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import { dataService } from '@/lib/dataService'
import { RefreshCw, AlertTriangle } from 'lucide-react'

export const ExamCalendar: React.FC = () => {
  const [sessions, setSessions] = useState<any[]>([])
  const [events, setEvents] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [googleConnected, setGoogleConnected] = useState(false)
  const [selectedSession, setSelectedSession] = useState<any | null>(null)

  const loadCalendarData = async () => {
    setLoading(true)
    try {
      const allSessions = await dataService.getAssessmentSessions()
      setSessions(allSessions)

      // Transform sessions into calendar events
      const calendarEvents = allSessions.map((session: any) => {
        return {
          id: session.id.toString(),
          title: `📅 เดดไลน์: ${session.name}`,
          start: session.end_date, // display on deadline day
          allDay: true,
          backgroundColor: session.is_open ? 'var(--primary)' : 'var(--text-muted)',
          borderColor: session.is_open ? 'var(--primary)' : 'var(--border)',
          textColor: '#ffffff',
          extendedProps: {
            sessionInfo: session
          }
        }
      })
      setEvents(calendarEvents)
    } catch (err) {
      console.error('Error loading calendar data:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadCalendarData()

    const checkGoogleToken = () => {
      const token = localStorage.getItem('google_drive_access_token')
      const expiry = localStorage.getItem('google_drive_token_expiry')
      if (token && expiry && Number(expiry) > Date.now()) {
        setGoogleConnected(true)
      } else {
        setGoogleConnected(false)
      }
    }
    checkGoogleToken()
    window.addEventListener('google-token-updated', checkGoogleToken)
    return () => window.removeEventListener('google-token-updated', checkGoogleToken)
  }, [])

  const handleSyncToGoogleCalendar = async () => {
    const token = localStorage.getItem('google_drive_access_token')
    if (!token) {
      alert('🔌 กรุณาเชื่อมบัญชี Google ของท่านที่ด้านบนของเมนูหลักก่อนทำการซิงค์ปฏิทิน')
      return
    }

    setSyncing(true)
    let successCount = 0
    let failCount = 0

    try {
      const activeSessions = sessions.filter(s => s.is_open)
      if (activeSessions.length === 0) {
        alert('ℹ️ ไม่มีรอบการป้อนคะแนนที่กำลังเปิดใช้งานในขณะนี้')
        setSyncing(false)
        return
      }

      for (const session of activeSessions) {
        // Create an event on Google Calendar
        const eventTitle = `🚨 [เดดไลน์ป้อนคะแนน] วันสุดท้ายส่งคะแนน: ${session.name}`
        const eventDesc = `กรุณากรอกคะแนน Pre-Test และ Post-Test ในระบบ TesScore ให้ครบก่อนปิดรอบ\nรอบการสอบ: ${session.name}\nวันที่เปิดระบบ: ${new Date(session.start_date).toLocaleDateString('th-TH')}\nวันสิ้นสุด: ${new Date(session.end_date).toLocaleDateString('th-TH')}`
        
        // Target deadline day (all day event)
        const deadlineDateStr = session.end_date.split('T')[0]

        const eventBody = {
          summary: eventTitle,
          description: eventDesc,
          start: {
            date: deadlineDateStr
          },
          end: {
            date: deadlineDateStr // all day event ends on same day
          },
          reminders: {
            useDefault: false,
            overrides: [
              { method: 'email', minutes: 24 * 60 }, // email reminder 1 day before
              { method: 'popup', minutes: 12 * 60 }  // popup reminder 12 hours before
            ]
          }
        }

        const res = await fetch('https://www.googleapis.com/calendar/v3/calendars/primary/events', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(eventBody)
        })

        if (res.ok) {
          successCount++
        } else {
          failCount++
          console.error('Failed to create calendar event:', await res.json())
        }
      }

      alert(`📅 ซิงค์ปฏิทินสำเร็จ! เพิ่มการเตือนเดดไลน์ส่งคะแนนเข้าไปยัง Google Calendar ของท่านจำนวน ${successCount} รายการ`)
    } catch (err: any) {
      alert(`❌ ซิงค์กำหนดการปฏิทินล้มเหลว: ${err.message}`)
    } finally {
      setSyncing(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      
      {/* Page Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 style={{ fontSize: '26px', fontWeight: '800', marginBottom: '6px', color: 'var(--text-primary)' }}>
            📅 ปฏิทินกำหนดการส่งคะแนน
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
            ติดตามวันสุดท้ายในการบันทึกคะแนน Pre-Test & Post-Test เพื่อช่วยเตือนความจำไม่ให้ลืมส่ง
          </p>
        </div>

        {/* Sync Controls */}
        <button
          type="button"
          onClick={handleSyncToGoogleCalendar}
          disabled={syncing}
          className="btn btn-primary"
          style={{
            background: 'linear-gradient(135deg, var(--primary) 0%, var(--primary-hover) 100%)',
            padding: '10px 18px',
            fontSize: '13px',
            fontWeight: '700',
            boxShadow: 'var(--shadow-glow)'
          }}
        >
          {syncing ? '⏳ กำลังซิงค์ข้อมูล...' : '🔁 ซิงค์กำหนดการเข้า Google Calendar'}
        </button>
      </div>

      {/* Warning Box when Google not connected */}
      {!googleConnected && (
        <div className="glassmorphism" style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '12px', background: 'rgba(245, 158, 11, 0.1)', borderColor: 'rgba(245, 158, 11, 0.2)', borderRadius: '10px' }}>
          <AlertTriangle size={20} color="var(--warning)" style={{ flexShrink: 0 }} />
          <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
            <strong>คำแนะนำการซิงค์ปฏิทิน:</strong> ท่านยังไม่ได้เชื่อมต่อบัญชี Google ของท่านกรุณาคลิกปุ่ม <strong>"🔑 เชื่อมบัญชี Google"</strong> ที่อยู่ด้านบนของหน้าเว็บก่อน เพื่อเปิดให้ระบบเพิ่ม Event ลงปฏิทินแจ้งเตือน Google Calendar อัตโนมัติได้
          </div>
        </div>
      )}

      {/* Main Calendar View Card */}
      <div className="glassmorphism" style={{ padding: '24px', background: 'var(--bg-card)' }}>
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '400px', gap: '12px' }}>
            <RefreshCw className="animate-spin" size={32} color="var(--primary)" />
            <span style={{ fontSize: '14px', color: 'var(--text-muted)' }}>กำลังโหลดปฏิทินกำหนดเวลา...</span>
          </div>
        ) : (
          <div className="calendar-dark-override" style={{ color: 'var(--text-primary)', fontSize: '14px' }}>
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              locale="th"
              events={events}
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek'
              }}
              buttonText={{
                today: 'วันนี้',
                month: 'รายเดือน',
                week: 'รายสัปดาห์'
              }}
              height="auto"
              eventClick={(info: any) => {
                const s = info.event.extendedProps.sessionInfo
                setSelectedSession(s)
              }}
            />
          </div>
        )}
      </div>

      {/* Custom styles override for FullCalendar to fit dark mode */}
      <style>{`
        .fc {
          --fc-button-bg-color: var(--bg-card);
          --fc-button-border-color: var(--border);
          --fc-button-text-color: var(--text-primary);
          --fc-button-hover-bg-color: var(--bg-card-hover);
          --fc-button-hover-border-color: var(--text-muted);
          --fc-button-active-bg-color: var(--primary);
          --fc-button-active-border-color: var(--primary);
          --fc-border-color: var(--border);
          --fc-page-bg-color: var(--bg-card);
          --fc-neutral-bg-color: var(--bg-base);
          --fc-list-event-hover-bg-color: var(--bg-card-hover);
        }
        .fc .fc-button-primary {
          background-color: var(--fc-button-bg-color);
          border-color: var(--fc-button-border-color);
          color: var(--fc-button-text-color);
          font-weight: 600;
          text-transform: none;
        }
        .fc .fc-button-primary:hover {
          background-color: var(--fc-button-hover-bg-color);
          border-color: var(--fc-button-hover-border-color);
          color: var(--fc-button-text-color);
        }
        .fc .fc-button-primary:disabled {
          background-color: var(--fc-button-bg-color);
          border-color: var(--fc-button-border-color);
          opacity: 0.5;
        }
        .fc .fc-button-active, .fc .fc-button-primary:not(:disabled).fc-button-active {
          background-color: var(--fc-button-active-bg-color) !important;
          border-color: var(--fc-button-active-border-color) !important;
          color: #ffffff !important;
        }
        .fc-theme-standard td, .fc-theme-standard th {
          border-color: var(--border) !important;
        }
        .fc-col-header-cell-cushion {
          color: var(--text-secondary) !important;
          font-weight: 600;
          padding: 8px 0 !important;
          text-decoration: none !important;
        }
        .fc-daygrid-day-number {
          color: var(--text-primary) !important;
          font-weight: 500;
          padding: 6px !important;
          text-decoration: none !important;
        }
        .fc-daygrid-day.fc-day-today {
          background-color: rgba(0, 184, 148, 0.08) !important;
        }
        .fc-event-title-container {
          padding: 2px 4px;
          cursor: pointer;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
      `}</style>

      {/* Modal Dialog for Event Details */}
      {selectedSession && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 17, 26, 0.7)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          animation: 'fadeIn 0.2s ease-out'
        }}>
          <div className="glassmorphism" style={{
            padding: '28px',
            width: '100%',
            maxWidth: '460px',
            position: 'relative',
            border: '1px solid rgba(99, 102, 241, 0.2)',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)',
            animation: 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
          }}>
            {/* Title / Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: '800', color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                📅 รายละเอียดรอบสอบ
              </h3>
              <button 
                onClick={() => setSelectedSession(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-secondary)',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '4px',
                  lineHeight: 1,
                  transition: 'color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--text-primary)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-secondary)'}
              >
                ✕
              </button>
            </div>

            {/* Content details */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
              <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '12px 16px', borderRadius: '8px', border: '1px solid var(--border)' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>ชื่อรอบการประเมิน</span>
                <span style={{ fontSize: '16px', fontWeight: '700', color: 'var(--primary-light)' }}>{selectedSession.name}</span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '12px 16px', borderRadius: '8px', border: '1px solid var(--border)' }}>
                  <span style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>📅 วันเริ่มบันทึกคะแนน</span>
                  <span style={{ fontSize: '14px', fontWeight: '600' }}>{new Date(selectedSession.start_date).toLocaleDateString('th-TH')}</span>
                </div>
                <div style={{ background: 'rgba(239, 68, 68, 0.03)', padding: '12px 16px', borderRadius: '8px', border: '1px solid rgba(239, 68, 68, 0.15)' }}>
                  <span style={{ fontSize: '12px', color: 'rgba(239, 68, 68, 0.9)', display: 'block', marginBottom: '4px' }}>🚨 วันสิ้นสุด (วันสุดท้าย)</span>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: 'rgba(239, 68, 68, 0.9)' }}>{new Date(selectedSession.end_date).toLocaleDateString('th-TH')}</span>
                </div>
              </div>

              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px', 
                background: selectedSession.is_open ? 'rgba(0, 184, 148, 0.05)' : 'rgba(239, 68, 68, 0.05)',
                padding: '12px 16px', 
                borderRadius: '8px',
                border: `1px solid ${selectedSession.is_open ? 'rgba(0, 184, 148, 0.15)' : 'rgba(239, 68, 68, 0.15)'}`
              }}>
                <span style={{ fontSize: '14px', fontWeight: '600', color: selectedSession.is_open ? 'var(--success)' : 'rgba(239, 68, 68, 0.9)' }}>
                  สถานะระบบ: {selectedSession.is_open ? '🟢 กำลังเปิดให้บันทึกคะแนน' : '🔴 ปิดระบบเรียบร้อยแล้ว'}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button 
                onClick={() => setSelectedSession(null)}
                className="btn btn-primary"
                style={{ padding: '10px 24px', fontWeight: '700', borderRadius: '8px' }}
              >
                ตกลง
              </button>
            </div>
          </div>
          <style>{`
            @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
            @keyframes slideUp { from { transform: translateY(12px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
          `}</style>
        </div>
      )}

    </div>
  )
}

export default ExamCalendar
